import { createNewTodoState, createNewListState } from './helpers';

// Serializes updateTodo calls so rapid bulk actions (e.g. checking several
// todos in quick succession) don't fire a burst of concurrent PUT requests,
// which was observed causing intermittent 500s (Bugsnag error 6a5cbb44fdca5cb0d6f889aa).
let updateQueue: Promise<unknown> = Promise.resolve();

export const useListsStore = defineStore('lists', {
    state: (): listsState => ({
        newTodo: createNewTodoState(),
        currentTodo: createNewTodoState(),
        newList: createNewListState(),
        currentList: createNewListState(),
        view: 'list',
        lists: [],
        todos: [],
        todaysTodos: [],
        overdueTodos: [],
        recentTodos: [],
        panelOpen: false,
    }),
    actions: {
        async addList(): Promise<List> {
            console.log('add list');

            if (this.newList) {
                try {
                    this.lists.push(this.newList);

                    const list = await $fetch<List>('/api/list', {
                        method: 'POST',
                        body: this.newList,
                    });

                    this.lists[this.lists.length - 1].id = list.id;

                    this.resetList();
                    return list;
                } catch (error: any) {
                    // Remove the list from the optimistic update
                    this.lists.pop();

                    // Set error using showError to trigger useError()
                    const errorMessage =
                        error?.data?.message || error?.message || 'Failed to create list';
                    showError({
                        statusCode: error?.statusCode || 500,
                        statusMessage: errorMessage,
                    });
                    throw error;
                }
            }
        },
        async updateList(listToUpdate?: List) {
            const { isOnline } = useOfflineSync();

            if (!listToUpdate) {
                listToUpdate = this.currentList;
            }

            if (!isOnline) {
                // logger.log('Skipping list update - offline')
                return;
            }

            await $fetch<List>(`/api/list/${listToUpdate.id}`, {
                method: 'PUT',
                body: listToUpdate,
            });
        },
        async deleteList(listId?: string) {
            if (!listId) {
                listId = this.currentList.id;
            }

            try {
                const route = useRoute();

                this.lists = this.lists.filter((list: List) => list.id !== listId);
                await $fetch<List>(`/api/list/${listId}`, {
                    method: 'DELETE',
                });

                // Only navigate away if we're currently viewing the list being deleted
                if (route.path.includes('/list/') && route.params?.id === listId) {
                    navigateTo('/');
                }
            } catch {
                // logger.error(err as Error, { component: 'ListsStore', function: 'deleteList', listId })
            }
        },
        setLists(lists: Array<List>) {
            this.lists = lists;
        },
        setListName(newName: string) {
            if (!this.currentList) {
                return;
            }
            this.currentList.name = newName;
        },
        setListTodos(todos: Task[]) {
            if (!todos || !todos.length) return;
            this.currentList.todos = todos;
        },
        async getListTodos(listId?: string): Promise<Task[]> {
            if (!listId) {
                listId = this.currentList.id;
            }
            const todos = await $fetch<Task[]>(`/api/list/todos`, { query: { listId } });
            const list = this.lists.find((l) => l.id === listId);
            if (list) {
                list.todos = todos;
            }
            if (this.currentList?.id === listId) {
                this.currentList.todos = todos;
            }
            return todos || [];
        },
        setCurrentList(list: List) {
            if (list) {
                this.currentList = list;
            }
        },
        async getCurrentList() {
            const route = useRoute();
            if (this.currentList.id == route.params.id) return;

            this.currentList = await $fetch(`/api/list/${route.params.id}`);
            console.log('get current list', this.currentList);
        },
        validateTodo(todo: Task) {
            let valid = true;
            if (!todo.name) {
                showError({
                    statusCode: 400,
                    statusMessage: 'Todo name is required',
                });
                valid = false;
            }
            return valid;
        },
        async addTodo(newTodo?: Task) {
            const todo =
                newTodo !== undefined && !(newTodo instanceof Event) ? newTodo : this.newTodo;
            console.debug('Create Todo', todo);

            if (!this.validateTodo(todo)) {
                return;
            }

            this.setTodoDetails(todo);

            this.optimisticallyUpdateTodos(todo);

            const createdTodo = await $fetch<Task>('/api/todo', {
                method: 'POST',
                body: todo,
            });

            console.debug('Todo created!', createdTodo);

            this.updateTodosId(createdTodo);

            this.resetTodo();

            return todo;
        },
        updateTodosId(todo: Task) {
            const route = useRoute();
            if (route.path.includes('list')) {
                this.currentList.todos[this.currentList.todos.length - 1].id = todo.id;
            } else {
                this.todaysTodos[this.todaysTodos.length - 1].id = todo.id;
            }
        },
        optimisticallyUpdateTodos(todo: Task) {
            const route = useRoute();
            if (route.path.includes('list')) {
                this.currentList.todos.push(todo);
            } else {
                this.todaysTodos.push(todo);
            }
        },
        setTodoDetails(todo: Task) {
            this.addListId(todo);

            this.setNewTodoDueDate(todo);
        },
        addListId(todo) {
            if (!todo.listId) {
                todo.listId = this?.currentList?.id;
            }
        },
        setNewTodoDueDate(todo: Task) {
            if (!todo.dueDate) {
                todo.dueDate = new Date();
            }
        },
        async updateTodo(todo?: Task) {
            if (!todo) {
                todo = this.currentTodo;
            }
            const targetTodo = todo;

            const run = () =>
                $fetch<Task>(`/api/todo/${targetTodo.id}`, {
                    method: 'PUT',
                    body: targetTodo,
                });
            updateQueue = updateQueue.then(run, run);
            const updatedTodo = (await updateQueue) as Task;

            // Update local state to match server response
            if (this.currentTodo && this.currentTodo.id === updatedTodo.id) {
                const existingSubtasks = this.currentTodo.subtasks;
                if (existingSubtasks) this.currentTodo.subtasks = existingSubtasks;
            }

            return updatedTodo;
        },
        async fetchSubtasks(todoId: string | number) {
            const subtasks = await $fetch<Task[]>(`/api/todo/${todoId}/subtasks`);
            if (this.currentTodo && Number(this.currentTodo.id) === Number(todoId)) {
                this.currentTodo.subtasks = subtasks || [];
            }
            return subtasks || [];
        },
        async addSubtask(name: string, parentId: string | number) {
            const subtask = await $fetch<Task>('/api/todo', {
                method: 'POST',
                body: {
                    name,
                    status: 'Open',
                    parentId: Number(parentId),
                    color: '#87909e',
                    edit: false,
                    links: [],
                },
            });
            if (this.currentTodo?.subtasks) this.currentTodo.subtasks.push(subtask);
            else if (this.currentTodo) this.currentTodo.subtasks = [subtask];
            return subtask;
        },
        async deleteSubtask(subtaskId: string | number) {
            await $fetch(`/api/todo/${subtaskId}`, { method: 'DELETE' });
            if (this.currentTodo?.subtasks) {
                this.currentTodo.subtasks = this.currentTodo.subtasks.filter(
                    (s: Task) => String(s.id) !== String(subtaskId),
                );
            }
        },
        debounceUpdateTodo: useDebounceFn(function (...args) {
            // @ts-expect-error: debounce context must be bound to the store, this workaround ensures correct `this`
            return (this.updateTodo as any).apply(this, args);
        }, 200),
        async deleteTodo(id: string) {
            await $fetch(`/api/todo/${id}`, { method: 'DELETE' });

            this.currentList.todos = this.currentList.todos.filter((todo: Task) => todo.id !== id);
            this.todaysTodos = this.todaysTodos.filter((todo: Task) => todo.id !== id);
            this.overdueTodos = this.overdueTodos.filter((todo: Task) => todo.id !== id);
            this.todos = this.todos.filter((todo: Task) => todo.id !== id);
            for (const list of this.lists) {
                if (list.todos) {
                    list.todos = list.todos.filter((todo: Task) => todo.id !== id);
                }
            }
        },
        async getTodo(id: string) {
            const { data } = await useFetch<Task>(`/api/todo/${id}`);

            if (data.value) {
                this.currentTodo = data.value;
            }

            return data;
        },
        async getTodos() {
            const { data } = await useFetch<Task[]>('/api/todos');

            if (data.value) {
                this.todos = data.value;
            }
        },

        setCurrentListName(name: string) {
            this.currentList.name = name;
        },
        setView(view: 'list' | 'board') {
            this.view = view;
        },
        setCurrentTodo(currentTodo: Task) {
            this.currentTodo = currentTodo;
        },
        setDueDate(date: Date) {
            if (this.currentTodo) {
                this.currentTodo.dueDate = date;
            }
        },
        setTaskName(name: string, index: number) {
            // Ensure the index is valid and the todo exists before setting the name
            if (
                !this.currentList ||
                !Array.isArray(this.currentList.todos) ||
                index < 0 ||
                index >= this.currentList.todos.length
            ) {
                return;
            }
            this.currentList.todos[index].name = name;
        },
        async getLists() {
            const lists = await $fetch<List[]>('/api/lists');
            this.setLists(lists);
        },
        async getList(id: string) {
            const { data } = await useFetch<List>(`/api/list/${id}`);

            if (data.value) {
                this.currentList = data.value;
                return this.currentList;
            }
        },
        async getTodaysTodos() {
            const todos = await $fetch<Task[]>('/api/todos', {
                query: { today: true },
            });

            if (todos) {
                this.todaysTodos = todos;
            }
        },
        async getRecentTodos() {
            const todos = await $fetch<Task[]>('/api/todos', {
                query: { recent: true },
            });
            if (todos) {
                this.recentTodos = todos;
            }
        },
        async getOverdueTodos() {
            const todos = await $fetch<Task[]>('/api/todos', {
                query: { overdue: true },
            });

            if (todos) {
                this.overdueTodos = todos;
            }
        },
        sortByDate(newDirection: string) {
            this.currentList.todos.sort((a, b) => {
                const dateA = a.dueDate ? new Date(a.dueDate).getTime() : null;
                const dateB = b.dueDate ? new Date(b.dueDate).getTime() : null;

                if (!dateA && !dateB) {
                    return 0;
                }
                if (!dateA) {
                    return -1;
                }
                if (!dateB) {
                    return -1;
                }
                const result = dateA - dateB;

                return newDirection === 'ascending' ? result : -result;
            });
        },
        newResetAll() {
            this.resetList();
            this.resetTodo();
        },
        resetTodo() {
            this.newTodo = createNewTodoState();
        },
        resetList() {
            this.newList = createNewListState();
        },
    },
});
